<script setup lang="ts">
  interface Props {
    title?: string;
    description?: string;
    mdiIcon?: string;
    svgIcon?: string;
    showBackSp?: boolean;
    isLibrary?: boolean;
    search?: string;
    showSearch?: boolean;
    isComplete?: boolean;
  }
  const localProps = withDefaults(defineProps<Props>(), {
    title: "",
    description: "",
    mdiIcon: "",
    svgIcon: "",
    showBackSp: true,
    isLibrary: false,
    search: "",
    showSearch: false,
    isComplete: false,
  });

  interface Emit {
    (event: "onBack"): void;
    (event: "update:search", search: string): void;
  }
  const emit = defineEmits<Emit>();

  // レスポンシブ対応
  const isTablet = isTabletAndSpDisplayRef();

  const showSearchLayout = ref(!!localProps.search);

  const searchModel = computed<string>({
    get: () => localProps.search,
    set: value => {
      emit("update:search", value);
    },
  });

  const handleShowSearch = () => {
    showSearchLayout.value = !showSearchLayout.value;
    if (!showSearchLayout.value) {
      searchModel.value = "";
    }
  };
</script>

<template>
  <div>
    <div class="header_title_wrapper" :class="{ 'is-library': isLibrary }">
      <div class="d-flex align-center">
        <template v-if="isTablet">
          <div class="back-btn-sp">
            <v-icon icon="$mdiChevronLeft" @click="$emit('onBack')" />
          </div>
          <div class="flex-grow-1">
            <slot name="header-title"></slot>
            <div class="header-title d-flex align-center justify-center">
              <AtomsSvgIcon v-if="!!svgIcon" class="mr-2" :width="20" :img-path="svgIcon" />
              <v-icon v-if="!!mdiIcon" class="mr-2" :size="20" :icon="mdiIcon" />
              <span v-if="!!title">{{ title }}</span>
              <span v-if="isComplete" class="library_complete">Complete!</span>
            </div>
            <span v-if="description" class="header-description d-flex align-center justify-center">
              {{ description }}
            </span>
          </div>

          <div class="action-btns">
            <slot name="header-btns"></slot>
            <v-icon
              v-if="showSearch"
              icon="$mdiMagnify"
              :color="showSearchLayout ? 'primary' : 'default'"
              @click="handleShowSearch"
            />
          </div>
        </template>

        <template v-else>
          <div class="d-flex align-center">
            <slot name="header-title"></slot>
            <AtomsSvgIcon v-if="!!svgIcon" class="mr-2" :width="20" :img-path="svgIcon" />
            <v-icon v-if="!!mdiIcon" class="mr-2" :size="20" :icon="mdiIcon" />
            <span v-if="!!title" class="header-title">{{ title }}</span>
          </div>
          <span v-if="description" class="header-description">
            {{ description }}
          </span>
          <div class="flex-grow-1"></div>
          <div class="action-btns">
            <slot name="header-btns"></slot>
            <v-icon
              v-if="showSearch"
              icon="$mdiMagnify"
              :size="28"
              class="ml-1"
              :color="showSearchLayout ? 'primary' : 'default'"
              @click="handleShowSearch"
            />
          </div>
        </template>
      </div>

      <slot name="header-extra"></slot>
    </div>

    <slot name="header-main-extra"></slot>

    <div v-if="showSearchLayout" class="popup-search">
      <div class="search-wrapper">
        <v-text-field
          v-model="searchModel"
          class="search-input"
          placeholder="キーワードを入力してください"
          prepend-inner-icon="$mdiMagnify"
          variant="solo"
          autofocus
          clearable
          @keydown.enter.prevent
        ></v-text-field>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .header_title_wrapper {
    padding: 10px 10px 10px 20px;
    border-bottom: 1px solid rgb(var(--v-theme-monochrome-08));
    color: #8a8a8a;
    @include display_sp {
      padding: 10px 20px;
    }
  }
  .header-title {
    color: white;
  }
  .back-btn-sp {
    width: 80px;
  }
  .action-btns {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    @include display_tablet {
      width: 80px;
    }
  }

  .is-library {
    @include display_pc {
      .header-title {
        font-size: 13px;
        font-weight: 700;
        color: rgb(var(--v-theme-monochrome-2));
      }
    }
    @include display_tablet {
      .header-title {
        text-align: center;
      }
      .header-description {
        text-align: center;
      }
    }
  }

  .popup-search {
    padding: 8px 12px;
    background: rgb(var(--v-theme-monochrome-04));
  }
  .search-wrapper {
    padding: 4px 2px;
  }
  .search-input {
    height: 40px;
    width: 100%;
    padding: 0;
  }
  :deep(.v-field--variant-solo) {
    background: rgb(var(--v-theme-monochrome-03));
    border-radius: 20px;
    height: 40px;
    @include display_tablet {
      border-radius: 4px;
    }
  }
  :deep(.v-field__field) {
    margin-top: -10px;
    height: 40px;
  }
</style>
